import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Bell, Check, CheckCheck, AlertTriangle, Sunrise, ShieldOff, ShieldCheck, UserX,
  Sparkles, CalendarClock, LifeBuoy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const ICON = {
  daily_start: Sunrise,
  missed_yesterday: AlertTriangle,
  two_day_miss_help: LifeBuoy,
  member_3day_miss: AlertTriangle,
  member_5day_miss: AlertTriangle,
  meeting_requested: CalendarClock,
  meeting_requested_ack: CalendarClock,
  access_suspended: ShieldOff,
  access_restored: ShieldCheck,
  access_terminated: UserX,
};

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [meetingOpen, setMeetingOpen] = useState(false);
  const [meetingNote, setMeetingNote] = useState("");

  const load = async () => {
    const { data } = await api.get("/notifications");
    setItems(data.items);
    setUnread(data.unread);
  };
  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`);
    load();
  };
  const markAll = async () => {
    await api.post("/notifications/read-all");
    toast.success("All caught up");
    load();
  };

  const askAI = (prompt) => {
    window.dispatchEvent(new CustomEvent("open-ask-ai", { detail: { prompt } }));
  };

  const requestMeeting = async () => {
    try {
      await api.post("/team/request-meeting", { note: meetingNote });
      toast.success("Your team leader has been notified");
      setMeetingOpen(false);
      setMeetingNote("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    }
  };

  return (
    <div className="p-8 md:p-10 max-w-3xl">
      <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#39FF14] mb-2">// Inbox</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight">
            <Bell className="inline mr-3 mb-2 text-[#39FF14]" size={40} /> Notifications
          </h1>
          <p className="text-sm text-neutral-500 mt-2">
            In-app reminders. <span className="text-neutral-400">Browser push & email delivery coming soon.</span>
          </p>
        </div>
        {unread > 0 && (
          <Button
            data-testid="mark-all-read-btn"
            onClick={markAll}
            variant="outline"
            className="rounded-none hairline bg-transparent hover:bg-[#111] hover:text-[#39FF14] text-xs uppercase tracking-widest h-10"
          >
            <CheckCheck size={14} className="mr-2" /> Mark all read
          </Button>
        )}
      </div>

      <div className="space-y-2">
        {items.length === 0 && (
          <div className="hairline bg-[#0F0F0F] p-10 text-center text-neutral-500 text-sm">
            No notifications yet. Log KPIs today and check back tomorrow.
          </div>
        )}
        {items.map((n) => {
          const Icon = ICON[n.type] || Bell;
          const isHelp = n.type === "two_day_miss_help";
          const isMeetingReq = n.type === "meeting_requested";
          return (
            <div
              key={n.notification_id}
              data-testid={`notif-${n.notification_id}`}
              className={`hairline bg-[#0F0F0F] p-5 flex gap-4 ${n.read && !isHelp ? "opacity-60" : ""}`}
            >
              <div className={`w-10 h-10 shrink-0 flex items-center justify-center ${
                isHelp ? "bg-[#39FF14] text-black" : (n.read ? "bg-[#1a1a1a] text-neutral-500" : "bg-[#39FF14] text-black")
              }`}>
                <Icon size={18} strokeWidth={2.5} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-3">
                  <div className="font-semibold">{n.title}</div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 shrink-0">
                    {new Date(n.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="text-sm text-neutral-400 mt-1">{n.body}</div>

                {/* Actionable buttons for 2-day miss */}
                {isHelp && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button
                      data-testid={`help-ai-${n.notification_id}`}
                      onClick={() => askAI("I've missed the last couple of workdays on my KPIs. Which specific category should I focus on first to get back on track, and how do I approach it today?")}
                      className="rounded-none bg-[#39FF14] text-black hover:bg-white text-xs uppercase tracking-widest h-9"
                    >
                      <Sparkles size={13} className="mr-2" /> Talk to Ask AI
                    </Button>
                    {n.meta?.has_leader && (
                      <Button
                        data-testid={`help-meeting-${n.notification_id}`}
                        onClick={() => setMeetingOpen(true)}
                        variant="outline"
                        className="rounded-none hairline bg-transparent hover:bg-[#111] hover:text-[#39FF14] text-xs uppercase tracking-widest h-9"
                      >
                        <CalendarClock size={13} className="mr-2" /> Request call with leader
                      </Button>
                    )}
                    {!n.read && (
                      <button
                        data-testid={`read-${n.notification_id}`}
                        onClick={() => markRead(n.notification_id)}
                        className="text-[10px] uppercase tracking-widest text-neutral-500 hover:text-white self-center ml-auto"
                      >
                        Dismiss
                      </button>
                    )}
                  </div>
                )}

                {/* Leader-facing meeting request → quick reply via AI-drafted message */}
                {isMeetingReq && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button
                      data-testid={`draft-reply-${n.notification_id}`}
                      onClick={() => askAI(`Draft a warm, short message to ${n.meta?.member_name || "my team member"} scheduling a 20-minute check-in call about their KPI progress. Suggest two time windows in the next 48 hours.`)}
                      className="rounded-none bg-[#39FF14] text-black hover:bg-white text-xs uppercase tracking-widest h-9"
                    >
                      <Sparkles size={13} className="mr-2" /> Draft reply with AI
                    </Button>
                    {!n.read && (
                      <button
                        data-testid={`read-${n.notification_id}`}
                        onClick={() => markRead(n.notification_id)}
                        className="text-[10px] uppercase tracking-widest text-neutral-500 hover:text-white self-center ml-auto"
                      >
                        Dismiss
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Simple read-check for non-actionable notifications */}
              {!isHelp && !isMeetingReq && !n.read && (
                <button
                  data-testid={`read-${n.notification_id}`}
                  onClick={() => markRead(n.notification_id)}
                  className="text-neutral-500 hover:text-[#39FF14] shrink-0"
                  title="Mark as read"
                >
                  <Check size={16} />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Request meeting dialog */}
      <Dialog open={meetingOpen} onOpenChange={setMeetingOpen}>
        <DialogContent data-testid="meeting-dialog" className="rounded-none bg-[#0F0F0F] hairline max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-widest text-sm">
              Request a call with your team leader
            </DialogTitle>
            <DialogDescription className="text-xs text-neutral-500">
              Your team leader will be notified in their Inbox. They'll reach out to schedule a time.
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1.5">
              Note (optional — helps them prep)
            </label>
            <Textarea
              data-testid="meeting-note"
              value={meetingNote}
              onChange={(e) => setMeetingNote(e.target.value)}
              placeholder="e.g. I've been struggling with cold-calls — I'd like to talk through my script."
              className="rounded-none bg-[#0A0A0A] hairline min-h-[90px]"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMeetingOpen(false)} className="rounded-none hairline bg-transparent">Cancel</Button>
            <Button
              data-testid="request-meeting-confirm"
              onClick={requestMeeting}
              className="rounded-none bg-[#39FF14] text-black hover:bg-white uppercase tracking-widest text-xs"
            >Send request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
